import { useState } from 'react';
import { useTranslation } from 'next-i18next';

const icon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 15C11.8012 15 11.6025 14.8964 11.5031 14.7927L8.2236 11.3731C7.92547 11.0622 7.92547 10.544 8.2236 10.2332C8.52174 9.92227 9.01863 9.92227 9.31677 10.2332L12 13.0311L14.6832 10.2332C14.9814 9.92227 15.4783 9.92227 15.7764 10.2332C16.0745 10.544 16.0745 11.0622 15.7764 11.3731L12.4969 14.6891C12.3975 14.8964 12.1988 15 12 15Z"
      fill="#0C0C0C"
    />
  </svg>
);

export default function Faqs() {
  const { t } = useTranslation('common');
  const dataList = [
    {
      q: 'What is the Dolphin Radar Affiliate Program and how does it work?',
      a: (
        <div>
          {t(
            'The Dolphin Radar Affiliate Program lets you earn money by sharing Dolphin Radar with others. You’ll get a unique referral link to promote our tools through your content—like blogs, videos, or social media. When someone signs up through your link and becomes a paying customer, you earn a commission.'
          )}
        </div>
      ),
    },
    {
      q: 'What is the commission structure and cookie length?',
      a: (
        <div>
          {t('The Dolphin Radar Affiliate Program offers a straightforward commission model: you’ll earn')}{' '}
          <span className="font-bold">{t('30% of every payment')}</span>{' '}
          {t('made by your referred customers during their first')}{' '}
          <span className="font-bold">{t('first 3 months.')}</span>
          {t('Each referral is tracked with a')} <span className="font-bold">30-day cookie</span>,{' '}
          {t('giving you ample time to earn from delayed conversions.')}
        </div>
      ),
    },
    {
      q: 'How much commission can I earn as an affiliate?',
      a: (
        <div>
          {t(
            'Dolphin Radar’s affiliate commission structure offers 30% commission for the first 3 months on all referred paid customers.'
          )}
        </div>
      ),
    },

    {
      q: 'What are the requirements for becoming an affiliate?',
      a: (
        <div>
          <p>
            {t(
              'Anyone can join the Dolphin Radar affiliate program—no special background or experience needed. We just ask that you promote Dolphin Radar in a fair and honest way.'
            )}
          </p>
          <p className="font-bold">{t('Please DO:')}</p>
          <ul>
            <li>1. {t('Share your unique referral link in blogs, articles, or on social media.')}</li>
            <li>2. {t('Promote Dolphin Radar honestly through content you genuinely support.')}</li>
          </ul>
          <p className="font-bold">{t("Please DON'T:")}</p>
          <ul>
            <li>1. {t('Share your link on adult, illegal, or harmful websites.')}</li>
            <li>2. {t('Use spammy, misleading, or dishonest tactics.')}</li>
            <li>3. {t('Pretend to be Dolphin Radar or mislead users about our product.')}</li>
            <li>4. {t('Abuse the program or attempt to cheat the system.')}</li>
          </ul>
        </div>
      ),
    },
    {
      q: 'How to join the Dolphin Radar affiliate program?',
      a: (
        <div>
          <p>
            {t('To get started, fill out the')}{' '}
            <a
              href="https://dolphin-radar.getrewardful.com/signup?campaign=dolphinradar-affiliate-user-30"
              target="_blank"
              className="text-blue-500"
            >
              {t('application')}
            </a>{' '}
            {t(
              'form with the required information. If you don’t have a Rewardful account, one will be created for you during signup. Once you sign up, you’ll gain access to your affiliate dashboard on Rewardful, where you can manage your referral links, track performance, and view commissions—all in one place. If you have any questions, feel free to reach out to us at'
            )}
            <a href="mailto:info.dolphinradar@gmail.com" className="text-blue-500">
              {' '}
              info.dolphinradar@gmail.com
            </a>
            .
          </p>
        </div>
      ),
    },
    {
      q: 'What is Dolphin Radar, and how do I promote it?',
      a: (
        <div>
          <div>
            <a href="https://dolphinradar.com" target="_blank" className="text-blue-500">
              Dolphin Radar
            </a>{' '}
            {t(
              'is a powerful tool for exploring Instagram activity—including story views, recent follows, and other public insights. It’s trusted by over 200,000 users for its ease of use, speed, and privacy-friendly experience.'
            )}
          </div>
          <div>
            {t(
              'You can promote Dolphin Radar by writing quick reviews, posting comparisons, or sharing useful tips on social media. Include your referral link in blog posts, tweets, or Instagram stories. Simple walkthrough videos or screenshots also work well to show how helpful the tool is.'
            )}
          </div>
        </div>
      ),
    },
    {
      q: 'Where can I find my affiliate link?',
      a: (
        <div>
          <p>
            {t(
              'Once you sign up through the application link, you’ll get instant access to your affiliate dashboard on Rewardful. Inside the dashboard, head to the'
            )}
            <span className="font-bold">“{t('Links')}”</span>{' '}
            {t('section — there, you’ll find your unique referral URL.')}
          </p>
          <p>{t('Your tracking text link should look like this example:')}</p>
          <p>https://www.dolphinradar.com/?via=11111({t('example only')})</p>
        </div>
      ),
    },
    {
      q: 'How and when will I get paid as an affiliate?',
      a: (
        <div>
          <p>
            {t(
              'Commissions are tracked and managed through Rewardful. Once a referred user makes a payment, your commission will be recorded and marked as pending. Payouts are processed after a 30-day hold period to account for refunds or disputes.'
            )}
          </p>
          <p>
            {t(
              'You’ll be paid via the method you selected in your affiliate settings (PayPal, Wise, or other). Make sure your payment info is up to date in the dashboard.'
            )}
          </p>
        </div>
      ),
    },
    {
      q: 'Is it free to join the Dolphin Radar affiliate program?',
      a: (
        <div>
          <p>
            {t('Absolutely. It’s 100% free to join, and you’ll earn commissions for every new paying user you refer.')}
          </p>
        </div>
      ),
    },
    {
      q: 'Do I need to have a certain audience size to become an affiliate?',
      a: (
        <div>
          {t(
            'Not at all. Whether you have a big reach or just a few friends who might be interested, you’re welcome to join. You don’t need a large following to start earning—just one person clicking your link and signing up can make a difference.'
          )}
        </div>
      ),
    },
    {
      q: 'Where can I get support if I have more questions?',
      a: (
        <div>
          <p>
            {t('If you have any questions about the Dolphin Radar affiliate program, feel free to reach out to us at')}{' '}
            <a href="mailto:info.dolphinradar@gmail.com" className="text-blue-500">
              info.dolphinradar@gmail.com.
            </a>
          </p>
        </div>
      ),
    },
  ];
  const [openIndex, setOpenIndex] = useState(0);
  const handleToggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };
  return (
    <div className="max-w-5xl mx-auto flex flex-col items-center lg:py-20 py-10 lg:px-0 px-4">
      <h2 className="lg:text-5xl text-3xl font-bold text-gray-900 text-center lg:mb-14 mb-5">
        {t('FrequentlyAskedQuestions')}
      </h2>
      <div className="w-full">
        {dataList.map((item, idx) => (
          <div key={idx} className={`${idx !== 0 && 'border-t'}`}>
            <h3
              className="w-full text-left py-4 flex justify-between items-center focus:outline-none text-lg cursor-pointer"
              onClick={() => handleToggle(idx)}
            >
              {t(item.q)}
              <span className={`${openIndex === idx ? 'rotate-180' : ''} transition-transform duration-300`}>
                {icon}
              </span>
            </h3>
            {openIndex === idx && <div className="pb-4 text-gray-600">{item.a}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
